import { clearSupabaseCookies } from "@/lib/utils/auth/clear-cookies";
import { logger } from "@/lib/logger";
import type { SupabaseBrowserClient } from "./types";

interface CreateAppleSignInHandlerArgs {
  supabase: SupabaseBrowserClient;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

export function createAppleSignInHandler(args: CreateAppleSignInHandlerArgs) {
  const { supabase, setIsLoading, setError, setSuccess } = args;

  return async (userType: "employee" | "client") => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Clear existing cookies before Apple sign-in
      const clearResult = clearSupabaseCookies();
      logger.info("Cleared cookies before Apple sign-in:", { clearResult });

      const currentOrigin = window.location.origin;
      const isLocalhost =
        currentOrigin.includes("localhost") || currentOrigin.includes("127.0.0.1");

      // CRITICAL: For OAuth, Supabase requires redirectTo to EXACTLY match
      // one of the Additional Redirect URLs (no query parameters allowed for matching).
      const redirectUrl = `${currentOrigin}/auth/callback`;

      logger.info("🍎 Apple OAuth redirect configuration:", {
        currentOrigin,
        isLocalhost,
        redirectUrl,
        userType,
        note: "Using clean URL without query params to match Supabase redirect URLs",
      });

      // Sign in with Apple using Supabase's built-in OAuth (browser client).
      // Apple returns the user's name only on the FIRST authorization, so request
      // the name + email scopes. Unlike Google, Apple ignores access_type/prompt.
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: {
          redirectTo: redirectUrl,
          scopes: "name email",
        },
      });

      if (error) {
        logger.error("Apple sign-in error", error);
        setError(error.message);
        return;
      }

      // The redirect happens automatically; no success message needed.
    } catch (err) {
      logger.error("Unexpected Apple sign-in error", err);
      setError("An unexpected error occurred during Apple sign-in");
    } finally {
      setIsLoading(false);
    }
  };
}
